import {
  Box,
  Button,
  Flex,
  ModalContent,
  ModalFooter,
  ModalHeader,
  SimpleGrid,
  Text,
  ModalContentProps,
  ModalBody,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TokenInput } from 'app/components/Core';
import { useIsMobile } from 'app/utils/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchema } from './utils';

export interface EmailVerificationForm {
  token: string;
}

export interface EmailVerificationDialogFormProps {
  onClose: () => void;
  verifyEmail: (values: EmailVerificationForm) => Promise<void>;
  modalContentProps?: ModalContentProps;
}

export const EmailVerificationDialogForm: React.FC<EmailVerificationDialogFormProps> = ({
  onClose,
  verifyEmail,
  modalContentProps,
}) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, isValid },
  } = useForm({
    defaultValues: {
      token: '',
    },
    mode: 'all',
    resolver: yupResolver(validationSchema),
  });
  const [tokenError, setTokenError] = useState('');

  const onSubmit = async (values: EmailVerificationForm) => {
    try {
      await verifyEmail(values);
      reset();
      onClose();
    } catch (e) {
      setTokenError('Entered code is invalid. Try again.');
    }
  };

  const isMobile = useIsMobile();

  return (
    <ModalContent {...modalContentProps}>
      <ModalHeader title="Email Verification">Email Verification</ModalHeader>
      <Box
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        data-testid="verify-email-form"
      >
        <ModalBody
          mb={4}
          px={4}
          py={2}
          overflow="auto"
          overscrollBehavior="none"
        >
          <SimpleGrid data-testid="dialog-content" spacingY={4}>
            <Box>
              <Text>
                We have sent you an email to confirm your address. Please check
                your inbox and enter the code provided.
              </Text>
            </Box>
            <Flex justifyContent="center">
              <Box maxWidth={392} width="100%">
                <Controller
                  control={control}
                  name="token"
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <TokenInput
                      onChange={(val) => {
                        if (tokenError) setTokenError('');
                        onChange(val);
                      }}
                      autoFocus={!isMobile}
                      error={error?.message || tokenError}
                      size={isMobile ? 'md' : 'lg'}
                    />
                  )}
                />
              </Box>
            </Flex>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} variant="outlined" mr={2}>
            Cancel
          </Button>
          <Button
            isDisabled={isSubmitting || !isValid || Boolean(tokenError)}
            type="submit"
            data-testid="submit-verify-email"
          >
            Verify
          </Button>
        </ModalFooter>
      </Box>
    </ModalContent>
  );
};
